/**
 * Select/Dropdown Component
 * Accessible form select with labels and validation
 */

import React from 'react';
import styles from './Select.module.css';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Options to display */
  options: SelectOption[];
  /** Placeholder text */
  placeholder?: string;
  /** Label text */
  label?: string;
  /** Helper text below select */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Whether field is required */
  isRequired?: boolean;
  /** Full width */
  isFullWidth?: boolean;
}

/**
 * Select Component
 *
 * @example
 * <Select
 *   label="Business Type"
 *   options={[
 *     { value: 'sole', label: 'Sole Proprietor' },
 *     { value: 'llc', label: 'LLC' },
 *     { value: 'corp', label: 'Corporation' },
 *   ]}
 *   isRequired
 * />
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      placeholder = 'Select an option',
      label,
      helperText,
      error,
      isRequired = false,
      isFullWidth = false,
      disabled = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;

    // Build select classes
    const selectClasses = `${styles.select} ${hasError ? styles.error : ''} ${className}`;

    // Build container classes
    const containerClasses = `${styles.container} ${isFullWidth ? styles.fullWidth : ''}`;

    return (
      <div className={containerClasses}>
        {label && (
          <label
            htmlFor={selectId}
            className={`${styles.label} ${isRequired ? styles.required : ''} ${
              disabled ? styles.disabled : ''
            }`}
          >
            {label}
            {isRequired && <span className={styles.required}> *</span>}
          </label>
        )}

        <div style={{ position: 'relative' }}>
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            className={selectClasses}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={`${option.value}`}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && <p className={styles.errorText}>{error}</p>}

        {helperText && !error && <p className={styles.helperText}>{helperText}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';
